import React from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Checkbox,
} from "@material-tailwind/react";
import { FormProvider, get, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import RHFInput from "../form/rhf-input";

// Lives inside the <Dialog> so every open remounts the form with fresh
// dates from the current survey prop (recipe P5); no reset effect is
// needed.
function RestartSurveyForm({
    survey,
    handleRestartDialogSubmit,
    handleClose,
}) {
    const validationSchema = Yup.object({
        start_date: Yup.string()
            .when('startNow', {
                is: false,
                then: (schema) => schema
                    .required("Start date is required")
                    .test(
                        "future-date",
                        "Start date must be in future",
                        (value) => {
                            return dayjs(value).isAfter(dayjs().subtract(1, 'minute'));
                        }
                    ),
                otherwise: (schema) => schema.strip(),
            }),
        end_date: Yup.string()
            .required("End date is required")
            .test(
                "future-date",
                "End date must be in future",
                (value) => {
                    return dayjs(value).isAfter(dayjs().subtract(1, 'minute'));
                }
            )
            .test(
                "after-start-date",
                "End date must be after start date",
                function(value) {
                    const { startNow } = this.parent;
                    // Skip check when starting now - backend handles start_date
                    if (startNow) return true;
                    const { start_date } = this.parent;
                    return dayjs(value).isAfter(dayjs(start_date));
                }
            ),
        startNow: Yup.boolean(),
    });

    const methods = useForm({
        mode: "onChange",
        // raw keeps the submitted values uncast. The start_date branch
        // ends with .strip(), so a bare yupResolver would submit Yup's
        // CAST object and silently DROP start_date whenever startNow is
        // true (recipe's raw-values rule).
        resolver: yupResolver(validationSchema, {}, { raw: true }),
        defaultValues: {
            start_date: survey?.start_date
                ? dayjs.utc(survey.start_date).local().format("YYYY-MM-DDTHH:mm")
                : "",
            end_date: survey?.end_date
                ? dayjs.utc(survey.end_date).local().format("YYYY-MM-DDTHH:mm")
                : "",
            startNow: false
        },
    });
    const { watch, setValue } = methods;
    // Destructure the formState flags up front (P1): formState is a
    // subscription proxy and conditional reads drop subscriptions.
    const { errors, isValid } = methods.formState;
    const startNow = watch("startNow");

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleRestartDialogSubmit)}>
                <DialogBody className="dialog__body">
                    <Typography className="mb-4">
                        To restart this survey, both start and end dates must be in future.
                        Please update the dates below.
                    </Typography>

                    {/* Start Date Section */}
                    <div className="mb-4">
                        <RHFInput
                            name="start_date"
                            type="datetime-local"
                            label="Start Date"
                            className="bg-white"
                            disabled={startNow}
                            autoFocus={false}
                            tabIndex="-1"
                        />
                        {get(errors, "start_date") && (
                            <Typography color="red" className="text-sm mt-1">
                                {get(errors, "start_date").message}
                            </Typography>
                        )}
                    </div>

                    {/* Start Now Checkbox */}
                    <div className="mb-4 flex items-center">
                        <Checkbox
                            checked={startNow}
                            label="Start survey now"
                            labelProps={{
                                className: "text-primary font-body",
                            }}
                            className="bg-white"
                            onChange={() => {
                                if (startNow) {
                                    setValue("startNow", false, { shouldDirty: true, shouldValidate: true });
                                } else {
                                    const now = dayjs().format("YYYY-MM-DDTHH:mm");
                                    setValue("start_date", now, { shouldDirty: true, shouldValidate: true });
                                    setValue("startNow", true, { shouldDirty: true, shouldValidate: true });
                                }
                            }}
                        />
                    </div>

                    {/* End Date Section */}
                    <div className="mb-4">
                        <RHFInput
                            name="end_date"
                            type="datetime-local"
                            label="End Date"
                            className="bg-white"
                            autoFocus={false}
                            tabIndex="-1"
                        />
                        {get(errors, "end_date") && (
                            <Typography color="red" className="text-sm mt-1">
                                {get(errors, "end_date").message}
                            </Typography>
                        )}
                    </div>

                    <Typography className="text-sm text-gray-600">
                        Restarting will set survey to Published status and allow
                        respondents to use existing survey invitation links once active.
                    </Typography>
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        className="btn btn--secondary"
                        variant="filled"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="btn btn--primary ml-2"
                        variant="filled"
                        disabled={!isValid}
                    >
                        Restart
                    </Button>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function RestartSurveyDialog({
    showDialog,
    handleClose,
    survey,
    handleRestartDialogSubmit,
}) {
    return (
        <Dialog
            open={showDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Restart Survey
            </DialogHeader>
            <RestartSurveyForm
                survey={survey}
                handleRestartDialogSubmit={handleRestartDialogSubmit}
                handleClose={handleClose}
            />
        </Dialog>
    );
}
